import { isMobile, isIE } from './utils/isMobile';
import parseUrl from './utils/parseUrl';
import {
  DHL_WEBCHAT_INIT,
  DHL_WEBCHAT_UPDATE_CONFIG,
  DHL_WEBCHAT_OPEN,
  DHL_WEBCHAT_CLOSE,
  DHL_WEBCHAT_SET_GEOLOCATION,
  DHL_WEBCHAT_TEST_MESSAGE,
  DHL_WEBCHAT_REMOVE_SHADOW,
  DHL_WEBCHAT_PUSH_ALERT
} from './constants';
import deepmerge from './utils/deepmerge';
import { getDocumentProperty } from './utils/document';
import { embedLocales } from './locales/embedLocales';

let currentScript;
if (document.currentScript) {
  currentScript = document.currentScript;
} else {
  // This is a polyfill for IE
  // This one works for async/defer scripts, but the parent page
  // needs to put script ID "dhl-va-widget" when embedding the embed.js to make it work
  // Example: <script id="dhl-va-widget" src="https://vawidget.dhl.com/latest/embed.js">
  if (document.getElementById('dhl-va-widget')) {
    currentScript = document.getElementById('dhl-va-widget');
  } else {
    // Otherwise we fallback to this one
    // It supports sync (none async/defer) scripts only, though
    // !! Do not remove, as some implementations are using this without the script ID..
    const scripts = document.getElementsByTagName('script');
    currentScript = scripts[scripts.length - 1];
  }
}

const url = parseUrl(currentScript.src);
const origin = url.origin;
// replace pathname /embed.js from the end with /index.html
const pathname =
  url.pathname.split('/').slice(0, -1).join('/') + '/index.html';

// Check, in which region is the code inclusion done to set managed config URL
// Default config region is EU
// Custom config URL always has priority
let configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_EU;
if (origin.indexOf('https://vawidget-eu.express.dhl') !== -1) {
  configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_EU_EXPRESS;
}
if (origin.indexOf('https://vawidget-ap.dhl.com') !== -1) {
  configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_AP;
}
if (origin.indexOf('https://vawidget-ap.express.dhl') !== -1) {
  configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_AP_EXPRESS;
}
if (origin.indexOf('https://vawidget-us.dhl.com') !== -1) {
  configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_US;
}
if (origin.indexOf('https://vawidget-us.express.dhl') !== -1) {
  configUrl = process.env.REACT_APP_MANAGED_CONFIG_URL_US_EXPRESS;
}
if (window.DHL_VAW_MANAGED_CONFIG_URL)
  configUrl = window.DHL_VAW_MANAGED_CONFIG_URL;

function initIframe(
  isFullScreen,
  isStartMaximized,
  isShowMinimizeIcon,
  config,
) {
  // optional fade-in animation
  if (isFullScreen && isStartMaximized && !isShowMinimizeIcon) {
    initFadeInAnimation();
  }
  const iframe = document.createElement('iframe');
  iframe.src = origin + pathname;
  // needed to allow speech recognition and fullscreen toggle in the widget
  iframe.allow = 'fullscreen; microphone';
  iframe.title = 'DHL VA Widget';
  iframe.id = 'dhl-va-widget-iframe';

  iframe.style.position = 'fixed';
  iframe.style.bottom = `${config.widgetIconOffsetY || 20}px`;
  iframe.style[config.widgetAnchor || 'right'] = `${
    config.widgetIconOffsetX || 30
  }px`;
  iframe.style.width = 0;
  iframe.style.maxWidth = 0;
  iframe.style.height = 0;
  iframe.style.maxHeight = 0;
  iframe.style.zIndex = 2147483005;
  iframe.style.border = 'none';
  iframe.style.outline = 'none';
  iframe.style.borderRadius = isFullScreen
    ? 0
    : config.design === 'mydhlplus'
    ? '4px 0px 0px 4px'
    : '8px';
  // this is an ie workaround to avoid flickering inside the iframe
  iframe.style.backgroundColor = 'rgba(0,0,0,0.003)';

  return iframe;
}

function maximizeIframe(
  iframe,
  isFullScreen,
  isBubble,
  isStartMaximized,
  isShowMinimizeIcon,
  config,
  isMinimizedExpanded
) {
  const isMyDHLPlusDesign = config.design === 'mydhlplus';

  // If we are starting in maximized state and in full screen, do a fade-in animation
  // Set animation for all browsers: https://stackoverflow.com/a/14585866
  if (isFullScreen && isStartMaximized && !isShowMinimizeIcon) {
    iframe.style.webkitAnimation = 'fadeinVAW 0.5s';
    iframe.style.MozAnimation = 'fadeinVAW 0.5s';
    iframe.style.OAnimation = 'fadeinVAW 0.5s';
    iframe.style.msAnimation = 'fadeinVAW 0.5s';
    iframe.style.animation = 'fadeinVAW 0.5s';
  }

  iframe.style.bottom = isBubble
    ? `${
        isMobile && !config.useWidgetOffsetOnMobile
          ? 20
          : config.widgetIconOffsetY || 20
      }px`
    : 0;
  iframe.style[config.widgetAnchor || 'right'] = isBubble
    ? `${
        isMobile && !config.useWidgetOffsetOnMobile
          ? 30
          : config.widgetIconOffsetX || 30
      }px`
    : 0;
  iframe.style.width = isBubble
    ? isMyDHLPlusDesign
      ? isMinimizedExpanded ?
        '300px': '70px'
      : '330px'
    : isFullScreen
    ? '100%'
    : isMyDHLPlusDesign
    ? 'calc(143/200*69vh)'
    : '500px';
  iframe.style.maxWidth = '100%';
  if (isMyDHLPlusDesign) {
    iframe.style.minWidth = isBubble ? '70px' : '350px';
  }
  iframe.style.height = isBubble
    ? isMyDHLPlusDesign
      ? '70px'
      : '240px'
    : isFullScreen
    ? '100%'
    : isMyDHLPlusDesign
    ? '69vh'
    : '700px';
  iframe.style.maxHeight = isFullScreen ? '100%' : '90%';
  iframe.style.boxShadow = isBubble
    ? 'none'
    : `${
        config.widgetAnchor === 'right' ? -1 : 1
      }px -1px 5px -1px rgba(0,0,0,0.1)`;
  iframe.style.transition = isBubble
    ? 'none'
    : 'box-shadow 0.8s ease-in';
  iframe.style.borderRadius = isFullScreen
    ? 0
    : isMyDHLPlusDesign
    ? '4px'
    : '8px';
}

function minimizeIframe(iframe, isHidden, config, isMinimizedExpanded) {
  const isMyDHLPlusDesign = config.design === 'mydhlplus';

  iframe.style.bottom = `calc(${
    isIE
      ? '0px'
      : 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))'
  } + ${
    isMobile && !config.useWidgetOffsetOnMobile
      ? 20
      : config.widgetIconOffsetY || 20
  }px)`;
  iframe.style[config.widgetAnchor || 'right'] = `${
    isMobile && !config.useWidgetOffsetOnMobile
      ? 30
      : config.widgetIconOffsetX || 30
  }px`;
  iframe.style.width = isHidden ? 0 
    : isMyDHLPlusDesign ? isMinimizedExpanded ? '300px': '70px' : '70px';

  iframe.style.maxWidth = isHidden ? 0 : '700px';
  iframe.style.height = isHidden ? 0 : '70px';
  iframe.style.maxHeight = isHidden ? 0 : '70px';
  iframe.style.boxShadow = 'none';
  if (isMyDHLPlusDesign) {
    iframe.style.minWidth = isHidden ? 0 : '70px';
  }
}

function removeShadow(iframe) {
  iframe.style.boxShadow = 'none';
  iframe.style.transition = 'none';
}

function initFadeInAnimation() {
  let animation = document.createElement('style');
  animation.type = 'text/css';
  const rules = document.createTextNode(`
  @keyframes fadeinVAW {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @-moz-keyframes fadeinVAW {
      from { opacity: 0; }
      to   { opacity: 1; }
  }

  @-webkit-keyframes fadeinVAW {
      from { opacity: 0; }
      to   { opacity: 1; }
  }

  @-ms-keyframes fadeinVAW {
      from { opacity: 0; }
      to   { opacity: 1; }
  }`);
  animation.appendChild(rules);
  document.getElementsByTagName('head')[0].appendChild(animation);
}

// Asynchronously get Geolocation of the user's IP and add it to the widget context using its API
// Asynchronous means that the geolocation information might not come at first VAP API request, but to subsequent requests only.
export function attemptToSetGeolocation() {
  let url = window.DHL_VAW_GEOLOCATON_API_URL
    ? window.DHL_VAW_GEOLOCATON_API_URL
    : process.env.REACT_APP_GEOLOCATON_API_URL;
  let req = new XMLHttpRequest();
  req.overrideMimeType('application/json');
  req.open('GET', url, true);
  req.onload = () => {
    try {
      let jsonResponse = JSON.parse(req.response);
      window.dhlVAWidget.setGeolocation(jsonResponse);
    } catch (e) {
      throw new Error(
        "DHLVAW: Couldn't parse/proceed with Geolocation API response. " +
          e,
      );
    }
  };
  req.onerror = () => {
    throw new Error("DHLVAW: Couldn't retrieve Geolocation.");
  };
  req.send(null);
}

function getManagedConfig(id, cb) {
  let url = configUrl + id + '.json';
  let req = new XMLHttpRequest();
  req.overrideMimeType('application/json');
  req.open('GET', url, true);
  req.onload = () => {
    try {
      let jsonResponse = JSON.parse(req.responseText);
      cb(jsonResponse);
    } catch (e) {
      cb({});
      throw new Error(
        "DHLVAW: Couldn't parse managed VA Widget configuration. Probably wrong ID was supplied. Will attempt to initliaze based on config passed directly.",
      );
    }
  };
  req.onerror = () => {
    throw new Error(
      "DHLVAW: Couldn't retrieve VA Widget configuration.",
    );
  };
  req.send(null);
}

function appendIframe(iframe) {
  document.body.appendChild(iframe);
}

function initVAWidgetConfig(finalConfig) {
  const message = {
    type: DHL_WEBCHAT_INIT,
    config: {
      ...finalConfig,
      locationHref: window.location.href,
    },
  };
  iframe.contentWindow.postMessage(JSON.stringify(message), origin);
}

// State controller for blinking tab alert
const browserNotification = {
  static: {
    documentTitle: document.title,
    interval: null,
    isAlertActive: false
  },
  on: function(notification, intervalSpeed) {
    const _this = this;
    this.static.isAlertActive = true;
    this.static.interval = setInterval(() => {
      document.title = _this.static.documentTitle === document.title
        ? notification
        : _this.static.documentTitle
    }, intervalSpeed ? intervalSpeed : 1000)
  },
  off: function() {
    clearInterval(this.static.interval);
    this.static.isAlertActive = false;
    document.title = this.static.documentTitle
  },
  
}

// Listen to the document visibility event.
// If isAlertActive is true and document is visible, it will turn off the blinking notification
function documentVisibilityEvent() {
  const { hidden, visibilityChange } = getDocumentProperty();
  if (!document.addEventListener  || !hidden) return;
  document.addEventListener(visibilityChange, () => {
    const isAlertActive = browserNotification.static.isAlertActive;
    if (!document[hidden] && isAlertActive) {
      browserNotification.off();
    }
  })
}

// Browser notification cannot be initialized on widget level due to api limitation.
// Chrome and Firefox no longer allow notification permissions to be requested from cross-origin <iframe>s,
// with other browsers to follow. Check this documentaion to know more https://developer.mozilla.org/en-US/docs/Web/API/notification
function pushAlertNotification(config) {
  // Different widget will have different ownership, therefore the message should be based on widget config
  const configLocales = embedLocales(config);
  if (!browserNotification.static.isAlertActive) {
    browserNotification.on(configLocales.tabWidgetNotificationMessage, 1000)
  }
  // Only trigger popup alert if the Notification API is available and the permission is granted
  if (!window.Notification) return;
  if (window.Notification.permission === 'granted') {
    const notification = new window.Notification(configLocales.popupWidgetNotificationMessage);
    notification.onclick = () => window.open('', '_self').focus();
  }
}
 
// By default, browser permission is set to "default"
// This is to ask user a permission to display a popup notification.
// check this documentaion to know more https://developer.mozilla.org/en-US/docs/Web/API/notification
function requestBrowserPopupAlert() {
  if (!window.Notification) return;
  if (window.Notification.permission !== 'granted') {
    window.Notification.requestPermission();
  }
}

function onIframeMessage(
  ev,
  isFullScreen,
  isStartMaximized,
  isShowMinimizeIcon,
  finalConfig,
) {
  try {
    const {
      type,
      isBubble,
      isMinimizedExpanded,
      config: iframeConfig
    } = JSON.parse(ev.data);
    const config = iframeConfig || finalConfig;

    if (type === DHL_WEBCHAT_OPEN) {
      maximizeIframe(
        iframe,
        isFullScreen,
        isBubble,
        isStartMaximized,
        isShowMinimizeIcon,
        finalConfig,
        isMinimizedExpanded,
      );
    } else if (type === DHL_WEBCHAT_CLOSE) {
      minimizeIframe(iframe, config.hidden, config, isMinimizedExpanded);
    } else if (type === DHL_WEBCHAT_SET_GEOLOCATION) {
      attemptToSetGeolocation();
    } else if (type === DHL_WEBCHAT_REMOVE_SHADOW) {
      removeShadow(iframe);
    } else if (type === DHL_WEBCHAT_PUSH_ALERT) {
      pushAlertNotification(config);
    }
  } catch (err) {
    // ignore malformed messages from other pages
  }
}

// Download and append custom script
function appendCustomScript(id, name, disableWidgetPopup) {
  let personality = id.split('/')[0];
  let scriptUrl = configUrl + personality + '/custom/' + name + '.js';
  let script = document.createElement('script');
  script.src = scriptUrl;
  script.type = 'text/javascript';
  script.setAttribute('data-vaw-ismobile', isMobile); // might be useful for the script
  script.setAttribute(
    'data-vaw-disablewidgetpopup',
    disableWidgetPopup,
  ); // used to decide whether to disable widget pop-up
  document.getElementsByTagName('head')[0].appendChild(script);
}

function initConfig(config) {
  // Sometimes, websites are loading the widget on all subsites
  // This allows us to control, on what pathnames we really want to show the widget
  if (
    config.showWidgetOnlyAtPathnames &&
    config.showWidgetOnlyAtPathnames.indexOf(
      window.location.pathname,
    ) === -1
  ) {
    return;
  }

  // additional condition type to control loading widgets on subsites
  // allows to have a restricted list (black list) of path names where widget should not be loaded
  if (
    !config.showWidgetOnlyAtPathnames &&
    config.showWidgetEverywhereExceptPathnames &&
    config.showWidgetEverywhereExceptPathnames.indexOf(
      window.location.pathname,
    ) !== -1
  ) {
    return;
  }

  const disableWidgetPopup =
    config.showWidgePopupOnlyAtPathnames &&
    config.showWidgePopupOnlyAtPathnames.indexOf(
      window.location.pathname,
    ) === -1;
  if (disableWidgetPopup) {
    config.widgetPopupOpenTimeout = -1;
  }

  // Also show at certain hashes only on load (see description of function)
  updateShowWidgetOnlyAtHashes(config);

  const isFullScreen = isMobile || config.fullScreen;
  const isStartMaximized = config.startMaximized;
  const isShowMinimizeIcon = config.showMinimizeIcon;
  iframe = initIframe(
    isFullScreen,
    isStartMaximized,
    isShowMinimizeIcon,
    config,
  );

  // In case configuration is retrieved after full page load, "load" is not fired anymore
  // This is a way how to append the iframe directly, then.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => appendIframe(iframe));
    if (config.customScript)
      appendCustomScript(
        config.id,
        config.customScript,
        disableWidgetPopup,
      );
  } else {
    appendIframe(iframe);
    if (config.customScript)
      appendCustomScript(
        config.id,
        config.customScript,
        disableWidgetPopup,
      );
  }
  iframe.addEventListener('load', () => {
    initVAWidgetConfig(config);

    // In case hash changes, we want to send the latest up-to-date location href
    // This listener should only be active after widget is inited
    window.addEventListener('hashchange', () => {
      window.dhlVAWidget._updateConfig({
        locationHref: window.location.href,
      });
      updateShowWidgetOnlyAtHashes(config);
    });
  });

  if (config.enableWidgetPushAlert) {
    requestBrowserPopupAlert();
  }
  
  documentVisibilityEvent();
  window.addEventListener('message', (ev) => {
    onIframeMessage(
      ev,
      isFullScreen,
      isStartMaximized,
      isShowMinimizeIcon,
      config,
    );
  });
}

// Specifically for myDHL+ site, we want to allow only certain hashes/anchors, since it is a single-page application
// This is allowed only on myDHL+ mobile site, since it would create undesired blinking effect on desktop (widget showing or not)
function updateShowWidgetOnlyAtHashes(config) {
  if (
    isMobile &&
    config.showWidgetOnlyAtHashes &&
    config.showWidgetOnlyAtHashes.indexOf(window.location.hash) === -1
  ) {
    let iframe = document.getElementById('dhl-va-widget-iframe');
    if (iframe) iframe.style.visibility = 'hidden';
  } else if (
    isMobile &&
    config.showWidgetOnlyAtHashes &&
    config.showWidgetOnlyAtHashes.indexOf(window.location.hash) !== -1
  ) {
    let iframe = document.getElementById('dhl-va-widget-iframe');
    if (iframe) iframe.style.visibility = 'visible';
  }
}

let iframe;

window.dhlVAWidget = {
  init(initialConfig = {}) {
    // One can disable managed config either via defining window.VAW_USE_MANAGED_CONFIG in parent page (good for self-hosting),
    // or during build time using .env file. Any of those will stop the managed config XHR request.
    if (
      (typeof window.DHL_VAW_USE_MANAGED_CONFIG === 'boolean' &&
        !window.DHL_VAW_USE_MANAGED_CONFIG) ||
      !process.env.REACT_APP_USE_MANAGED_CONFIG
    ) {
      initConfig(initialConfig);
    } else {
      // If legacy init is used, we can concatenate it
      if (initialConfig.accessToken && initialConfig.personality) {
        // ID is used by app storage, we need to have it in config
        initialConfig.id =
          initialConfig.personality + '/' + initialConfig.accessToken;
      }
      getManagedConfig(initialConfig.id, (managedConfig) => {
        // Deeply merging the object instead of Object.assign(target, source)
        // This is necessary in case both managed and initial config are deeply nested
        let finalConfig = deepmerge(managedConfig, initialConfig);
        initConfig(finalConfig);
      });
    }
  },
  open() {
    if (!iframe) {
      throw new Error(
        'DHLVAW: You have to init the chat before opening it',
      );
    }

    const message = { type: DHL_WEBCHAT_OPEN };
    iframe.contentWindow.postMessage(JSON.stringify(message), origin);
  },
  close() {
    if (!iframe) {
      throw new Error(
        'DHLVAW: You have to init the chat before closing it',
      );
    }

    const message = { type: DHL_WEBCHAT_CLOSE };
    iframe.contentWindow.postMessage(JSON.stringify(message), origin);
  },
  _updateConfig(extraConfig) {
    if (!iframe) {
      throw new Error(
        'DHLVAW: You have to init the chat before add dynamic configuration',
      );
    }

    const message = {
      type: DHL_WEBCHAT_UPDATE_CONFIG,
      config: extraConfig,
    };
    iframe.contentWindow.postMessage(JSON.stringify(message), origin);
  },
  setLanguageCode(languageCode) {
    this._updateConfig({ languageCode });
  },
  setCountryCode(countryCode) {
    this._updateConfig({ countryCode });
  },
  // Please be aware that setting deeply nested custom parameters more than once is not supported yet
  setCustomParameters(customParameters) {
    this._updateConfig({ customParameters });
  },
  setGeolocation(geolocation) {
    this._updateConfig({ geolocation });
  },
  testMessage(type) {
    if (!iframe) {
      throw new Error(
        'DHLVAW: You have to init the chat before testing messages',
      );
    }

    const message = {
      type: DHL_WEBCHAT_TEST_MESSAGE,
      testMessage: type,
    };
    iframe.contentWindow.postMessage(JSON.stringify(message), origin);
  },
};
